import { useContext, useEffect, useState } from 'react';
import {
  alpha,
  Backdrop,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Fade,
  Grid,
  Typography,
  useTheme,
} from '@mui/material';
import { BarChart, PieChart } from '@mui/x-charts';
import { useForm } from 'react-hook-form';
import { DashboardContext } from 'contexts/DashboardContext';
import Container from 'components/Container';
import InputMask from 'components/InputMask';
import Button from 'components/Button';
import moment from 'moment';
import styles from './styles';

const Pie = ({ data = [] }) => {
  const theme = useTheme();

  const pieSeries = (data) => [
    {
      data,
      innerRadius: 40,
      outerRadius: 80,
      paddingAngle: 4,
      cornerRadius: 5,
    },
  ];

  const pseudoRandom = (seed) => {
    let x = Math.sin(seed) * 10000;
    return x - Math.floor(x);
  };

  const generateThemeColors = (arr = []) => {
    const length = arr?.length;
    const baseColors = [
      theme?.palette?.primary?.light,
      theme?.palette?.primary?.main,
      theme?.palette?.primary?.dark,
    ];

    const alphas = Array.from(
      { length },
      (_, i) => 0.2 + pseudoRandom(i) * 0.9
    );

    return Array.from({ length }, (_, i) => {
      if (arr?.[i]?.color) {
        return arr?.[i]?.color;
      }
      const base = baseColors[i % baseColors.length];
      const opacity = +alphas[i].toFixed(2);
      return alpha(base, opacity);
    });
  };

  if (!data?.some((item) => item?.value > 0)) {
    return (
      <Typography variant="body2" textAlign="center">
        Sem dados para exibir
      </Typography>
    );
  }

  return (
    <Box display="flex">
      <PieChart
        height={250}
        margin={styles?.chartMargin}
        slotProps={styles?.chartSlot}
        series={pieSeries(data)}
        colors={generateThemeColors(data)}
      />
      <Box sx={styles?.legendContainer}>
        {data?.map((item, index) => (
          <Box key={index} display="flex" gap={1}>
            <Box
              sx={styles?.legendBox}
              bgcolor={generateThemeColors(data)?.[index]}
            />
            <Typography variant="caption" alignSelf="center">
              {item?.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const App = () => {
  const defaultValues = {
    dti: moment().startOf('year').format('YYYY-MM-DD'),
    dtf: null,
  };
  const { getLoading, getDashboard } = useContext(DashboardContext);
  const { control, handleSubmit } = useForm({ defaultValues });
  const [filtros, setFiltros] = useState(null);
  const [data, setData] = useState(null);
  const theme = useTheme();

  const cardsSituacao = [
    {
      title: 'Itens Armazenados',
      value: data?.situacao?.quantidade_armazenados || 0,
    },
    {
      title: 'Valor de Custo',
      value: (data?.situacao?.preco_armazenados || 0)?.toLocaleString('pt-br', {
        style: 'currency',
        currency: 'BRL',
      }),
    },
  ];

  const chartsSituacao = [
    {
      title: `Cubagem (${(data?.situacao?.cubagem_total || 0)?.toLocaleString(
        'pt-br',
        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      )} m³)`,
      data: [
        {
          label: 'Livre',
          value:
            data?.situacao?.cubagem_total - data?.situacao?.cubagem_utilizada,
        },
        { label: 'Ocupada', value: data?.situacao?.cubagem_utilizada },
      ],
    },
    {
      title: 'Pedidos',
      data: [
        { label: 'Pendentes', value: data?.situacao?.pedidos?.pendentes },
        { label: 'Separação', value: data?.situacao?.pedidos?.separacao },
        { label: 'Embalagem', value: data?.situacao?.pedidos?.embalagem },
        { label: 'Concluídos', value: data?.situacao?.pedidos?.concluidos },
      ],
    },
  ];

  const cardsResumo = [
    {
      title: 'Itens Armazenados',
      value: data?.resumo?.armazenagem?.total || 0,
    },
    { title: 'Itens Expedidos', value: data?.resumo?.expedicao?.total || 0 },
    { title: 'Pedidos', value: data?.resumo?.pedidos?.total || 0 },
    { title: 'Embalagens', value: data?.resumo?.embalagem?.total || 0 },
    // { title: 'Volumes', value: data?.resumo?.volume?.total || 0 },
  ];

  const dataEmbalagens = data?.resumo?.embalagem?.tipos || [];

  const barSeries = [
    {
      label: 'Armazenado',
      dataKey: 'armazenado',
      color: theme.palette.primary.dark,
    },
    {
      label: 'Expedido',
      dataKey: 'expedido',
      color: theme.palette.primary.light,
    },
  ];

  const barAxis = [{ scaleType: 'band', dataKey: 'mes' }];

  const onSubmit = (params) => {
    const cb = (res) => {
      setData(res);
      setFiltros(params);
    };
    getDashboard({ params, cb });
  };

  const generateSubtitle = () => {
    let subtitle = 'De';
    if (filtros?.dti) {
      subtitle += ` ${moment(filtros?.dti).format('DD/MM/YYYY')}`;
    } else {
      subtitle += ` ${moment().format('DD/MM/YYYY')}`;
    }
    if (filtros?.dtf) {
      subtitle += ` até ${moment(filtros?.dtf).format('DD/MM/YYYY')}`;
    } else {
      subtitle += ` até ${moment().format('DD/MM/YYYY')}`;
    }
    return subtitle;
  };

  const generateChart = () => {
    const expedicaoMensal = data?.resumo?.expedicao?.mensal || [];
    const armazenagemMensal = data?.resumo?.armazenagem?.mensal || [];

    const dataInicio = moment(filtros?.dti || new Date()).startOf('month');
    const dataFim = moment(filtros?.dtf || new Date()).endOf('month');

    const meses = [];
    const cursor = dataInicio.clone();

    while (cursor.isSameOrBefore(dataFim, 'month')) {
      const mes = cursor.month() + 1;

      const armazenado =
        armazenagemMensal.find((m) => m.mes === mes)?.quantidade_armazenada ||
        0;

      const expedido =
        expedicaoMensal.find((m) => m.mes === mes)?.quantidade_expedida || 0;

      meses.push({
        mes: cursor
          .format('MMM/YY')
          .replace('.', '')
          .replace(/^\w/, (c) => c.toUpperCase()),
        armazenado,
        expedido,
      });

      cursor.add(1, 'month');
    }

    return meses;
  };

  useEffect(() => {
    handleSubmit(onSubmit)();
  }, []);

  return (
    <Container>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6" fontWeight="bold">
            Situação do Armazém
          </Typography>
        </Grid>
        {chartsSituacao?.map((chart, i) => (
          <Grid item xs={12} sm={6} md={4} key={i}>
            <Card sx={styles?.chart}>
              <CardContent>
                <Typography variant="h6" textAlign="center" mb={2}>
                  {chart?.title}
                </Typography>
                <Pie data={chart?.data} />
              </CardContent>
            </Card>
          </Grid>
        ))}
        <Grid item xs={12} md={4} display="flex">
          <Box display="flex" flexDirection="column" flex={1} gap={2}>
            {cardsSituacao?.map((card, i) => (
              <Card sx={styles?.flexCard} key={i}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    {card?.title}
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {card?.value}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Grid>
        <Grid item xs={12} mt={4}>
          <Typography variant="h6" fontWeight="bold">
            Resumo Logístico
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            {generateSubtitle()}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4} md={3}>
                <InputMask
                  type="date"
                  name="dti"
                  control={control}
                  label="Data Inicial"
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <InputMask
                  type="date"
                  name="dtf"
                  control={control}
                  label="Data Final"
                />
              </Grid>
              <Grid item xs={12} sm="auto" display="flex" alignItems="center">
                <Button
                  type="submit"
                  sx={styles?.button}
                  variant="contained"
                  onClick={handleSubmit(onSubmit)}
                >
                  Aplicar Filtro
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Grid>
        {cardsResumo?.map((card, i) => (
          <Grid item xs={12} sm={6} lg={12 / cardsResumo?.length} key={i}>
            <Card sx={styles?.card}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  {card?.title}
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {card?.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
        <Grid item xs={12} lg={4} mb={4}>
          <Card sx={styles?.chart}>
            <CardContent>
              <Typography variant="h6" textAlign="center" mb={2}>
                Tipos de Embalagens
              </Typography>
              <Pie data={dataEmbalagens} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={8} mb={4}>
          <Card sx={styles?.chart}>
            <CardContent>
              <Typography variant="h6" textAlign="center" mb={2}>
                Itens Armazenados vs Expedidos
              </Typography>
              <BarChart
                dataset={generateChart()}
                series={barSeries}
                xAxis={barAxis}
                height={300}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Fade in={getLoading}>
        <Backdrop open={getLoading} sx={styles?.loader}>
          <CircularProgress color="inherit" size={48} thickness={4} />
        </Backdrop>
      </Fade>
    </Container>
  );
};

export default App;
